import React, { useState } from 'react';
import { motion } from 'framer-motion';

const PromptIntelligence = ({ data }) => {
  const [activeTab, setActiveTab] = useState('overview');
  if (!data) return null;

  const getPriorityColor = (p) => ({ High: '#ef4444', Medium: '#f97316', Low: '#22c55e' }[p] || '#d1d5db');
  const getOptColor = (o) => ({ Optimized: '#10b981', 'Partially Optimized': '#eab308', 'Not Optimized': '#ef4444' }[o] || '#d1d5db');
  const getQualityColor = (q) => ({ Excellent: '#10b981', Good: '#22c55e', Fair: '#eab308', Poor: '#ef4444' }[q] || '#d1d5db');
  const getDiffColor = (d) => ({ Easy: '#10b981', Medium: '#eab308', Hard: '#ef4444' }[d] || '#d1d5db');

  const NotMeasured = ({ label, reason }) => (
    <div style={{ textAlign: 'center', padding: '18px 12px' }}>
      <div style={{ color: '#6b7280', fontSize: 30, fontWeight: 700, lineHeight: 1 }}>&mdash;</div>
      {label ? <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 8 }}>{label}</div> : null}
      <div style={{ color: '#6b7280', fontSize: 11, marginTop: 4, maxWidth: 240, margin: '4px auto 0' }}>
        {reason || 'Not measured'}
      </div>
    </div>
  );

  // No interface reports an AI prompt score, so none is drawn.
  // Previously this rendered a gauge from a model-authored number.
  const renderScoreGauge = () => (
    <NotMeasured label="Prompt Score" reason="No measurement source exists for AI prompt visibility" />
  );

  // Categories are qualitative themes only. The counts and percentages that
  // drove this donut were model output and summed to a round invented total.
  const renderCategoryDonut = () => {
    const cats = data.promptCategories || [];
    if (!cats.length) {
      return <NotMeasured label="Prompt Categories" reason="No categories returned for this domain" />;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {cats.map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.color || '#6366f1', flexShrink: 0 }} />
            <span style={{ color: '#d1d5db', fontSize: 14 }}>{c.category}</span>
          </div>
        ))}
        <div style={{ color: '#6b7280', fontSize: 11, marginTop: 8 }}>
          Themes only &mdash; prompt volume per category is not measurable.
        </div>
      </div>
    );
  };

  const renderOptimizationBars = () => {
    const opts = data.promptOptimization || [];
    if (!opts.length) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {opts.map((o, i) => (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#d1d5db', fontSize: 16, fontWeight: 600 }}>{o.area}</span>
              <span style={{ color: '#d1d5db', fontSize: 15 }}>{o.currentScore}%  <span style={{ color: '#10b981' }}>{o.potentialScore}%</span></span>
            </div>
            <div style={{ background: '#1f2937', borderRadius: 6, height: 10, overflow: 'hidden', position: 'relative' }}>
              <div style={{ width: `${o.potentialScore}%`, height: '100%', background: '#1f3a2f', borderRadius: 6, position: 'absolute' }} />
              <motion.div initial={{ width: 0 }} animate={{ width: `${o.currentScore}%` }} transition={{ duration: 1, delay: i * 0.15 }}
                style={{ height: '100%', borderRadius: 6, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', position: 'relative', zIndex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
              {o.actions?.map((a, j) => (
                <span key={j} style={{ background: '#1f2937', color: '#d1d5db', padding: '2px 6px', borderRadius: 8, fontSize: 13 }}>{a}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'prompts', label: 'Prompts & Patterns' },
    { id: 'platforms', label: 'Platforms & Optimization' }
  ];

  return (
    <div style={{ color: '#fff' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 600,
              background: activeTab === tab.id ? '#6366f1' : '#1f2937', color: activeTab === tab.id ? '#fff' : '#d1d5db', transition: 'all 0.2s' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20, textAlign: 'center' }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Prompt Score</h4>
              {renderScoreGauge()}
            </div>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Key Metrics</h4>
              {[{ label: 'Total Prompts', value: null, note: 'No source measures AI prompt volume' },
                { label: 'Avg Complexity', value: data.avgComplexity || null, note: 'Qualitative assessment, not a count' },
                { label: 'Growth', value: null, note: 'No measured baseline to compare against' }
              ].map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 2 ? '1px solid #1f2937' : 'none' }}>
                  <span style={{ color: '#d1d5db', fontSize: 16 }}>{m.label}{m.note ? <span style={{ display: 'block', color: '#6b7280', fontSize: 11 }}>{m.note}</span> : null}</span>
                  <span style={{ color: '#6366f1', fontSize: 16, fontWeight: 600 }}>{(m.value === null || m.value === undefined) ? '—' : (typeof m.value === 'number' ? m.value.toLocaleString() : m.value)}</span>
                </div>
              ))}
            </div>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Prompt Categories</h4>
              {renderCategoryDonut()}
            </div>
          </div>

          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Emerging Prompt Trends</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
              {(data.emergingPrompts || []).map((ep, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{ep.prompt}</span>
                          <span style={{ color: '#6b7280', fontSize: 12 }}>growth not measured</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <span style={{ background: getPriorityColor(ep.relevance === 'High' ? 'Low' : ep.relevance === 'Low' ? 'High' : 'Medium') + '22', color: getPriorityColor(ep.relevance === 'High' ? 'Low' : ep.relevance === 'Low' ? 'High' : 'Medium'), padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>
                      {ep.relevance} Relevance
                    </span>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 14 }}>{ep.action}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Prompt Optimization Areas</h4>
            {renderOptimizationBars()}
          </div>
        </motion.div>
      )}

      {activeTab === 'prompts' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24, overflowX: 'auto' }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Top AI Prompts</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(data.topPrompts || []).map((p, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, borderLeft: `3px solid ${getOptColor(p.optimizationLevel)}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                    <span style={{ color: '#d1d5db', fontSize: 16, fontWeight: 600, flex: 1, minWidth: 200 }}>"{p.prompt}"</span>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <span style={{ background: '#1f2937', color: '#6366f1', padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>{p.category}</span>
                      <span style={{ background: '#1f2937', color: p.frequency === 'High' ? '#ef4444' : p.frequency === 'Medium' ? '#f97316' : '#22c55e', padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>{p.frequency}</span>
                      {p.brandMention && <span style={{ background: '#10b98122', color: '#10b981', padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>Brand </span>}
                    </div>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 14, marginBottom: 4 }}>AI Response: {p.aiResponse}</p>
                  <span style={{ color: getOptColor(p.optimizationLevel), fontSize: 14, fontWeight: 600 }}>{p.optimizationLevel}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Prompt Patterns</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
              {(data.promptPatterns || []).map((pp, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{pp.pattern}</span>
                    <span style={{ color: getPriorityColor(pp.brandOpportunity === 'High' ? 'Low' : pp.brandOpportunity === 'Low' ? 'High' : 'Medium'), fontSize: 14, fontWeight: 600 }}>{pp.brandOpportunity} Opportunity</span>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15, marginBottom: 8 }}>{pp.description}</p>
                  {pp.examples?.map((ex, j) => (
                    <p key={j} style={{ color: '#6366f1', fontSize: 14, padding: '3px 0', fontStyle: 'italic' }}> "{ex}"</p>
                  ))}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {activeTab === 'platforms' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Platform Behavior Analysis</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
              {(data.platformBehavior || []).map((pb, i) => {
                const colors = { ChatGPT: '#10b981', Claude: '#f97316', Perplexity: '#8b5cf6', 'Google AI': '#3b82f6', 'Bing Chat': '#06b6d4' };
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                    style={{ background: '#0d1117', borderRadius: 10, padding: 16, borderTop: `3px solid ${colors[pb.platform] || '#6366f1'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>{pb.platform}</span>
                      <span style={{ background: '#1f2937', color: colors[pb.platform] || '#6366f1', padding: '3px 10px', borderRadius: 10, fontSize: 15, fontWeight: 600 }}>Not available</span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ color: '#d1d5db', fontSize: 14 }}>Visibility</span>
                        <span style={{ color: '#6b7280', fontSize: 14 }}>Not available</span>
                      </div>
                      <div style={{ color: '#6b7280', fontSize: 11 }}>
                        No public interface reports brand visibility for {pb.platform}.
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      <span style={{ background: getQualityColor(pb.responseQuality) + '22', color: getQualityColor(pb.responseQuality), padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>
                        {pb.responseQuality} Response
                      </span>
                    </div>
                    <p style={{ color: '#d1d5db', fontSize: 14, marginBottom: 4 }}><strong style={{ color: '#d1d5db' }}>Style:</strong> {pb.promptStyle}</p>
                    <p style={{ color: '#6366f1', fontSize: 14, fontStyle: 'italic' }}>{pb.keyInsight}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Recommendations</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(data.recommendations || []).map((rec, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{rec.title}</span>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <span style={{ background: getPriorityColor(rec.priority) + '22', color: getPriorityColor(rec.priority), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{rec.priority}</span>
                      <span style={{ background: getDiffColor(rec.difficulty) + '22', color: getDiffColor(rec.difficulty), padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>{rec.difficulty}</span>
                    </div>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15, marginBottom: 6 }}>{rec.description}</p>
                  <p style={{ color: '#6366f1', fontSize: 14, fontStyle: 'italic' }}>Expected: {rec.expectedImpact}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PromptIntelligence;
